// ---------------------------------------------------------------------------
// ตัวเชื่อม Firebase Realtime Database ผ่าน REST (ไม่มี server — หน้าเว็บยิงตรง)
// ---------------------------------------------------------------------------
// เปิดใช้เมื่อตั้ง REACT_APP_FIREBASE_URL (ลำดับ: Firebase > Google Sheets > localStorage)
// ต้องวาง database.rules.json ใน Firebase console ก่อน (ดู README ของ repo)
// ---------------------------------------------------------------------------

const baseUrl = () => String(process.env.REACT_APP_FIREBASE_URL || "").trim().replace(/\/+$/, "");

export const FB_URL = baseUrl();

/** เปิดใช้โหมด Firebase หรือยัง */
export const fbEnabled = () => !!baseUrl();

export class FbError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "FbError";
    this.status = status;
  }
}

/** คีย์ใน RTDB ห้ามมี . $ # [ ] / และตัวควบคุม — เข้ารหัสเป็น %XX (id จริงยังอยู่ในฟิลด์ id ของแถว) */
export const encodeKey = (id) =>
  // eslint-disable-next-line no-control-regex -- ตั้งใจเข้ารหัสอักขระควบคุมที่ RTDB ห้ามใช้ใน key
  String(id).replace(/[.$#[\]/%\u0000-\u001f\u007f]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`);

const buildUrl = (path, query = {}) => {
  const clean = String(path || "").replace(/^\/+|\/+$/g, "");
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return `${baseUrl()}/${clean}.json${qs ? `?${qs}` : ""}`;
};

const failFrom = async (response) => {
  let message = `Firebase ตอบกลับสถานะ ${response.status}`;
  try {
    const payload = await response.json();
    if (payload && payload.error) message = `Firebase: ${payload.error}`;
  } catch (e) {
    /* ไม่ใช่ JSON */
  }
  return new FbError(message, response.status);
};

const send = async (method, path, { query, body, headers } = {}) => {
  if (!fbEnabled()) throw new FbError("ยังไม่ได้ตั้งค่า REACT_APP_FIREBASE_URL");
  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return response;
};

/** อ่านข้อมูลที่ path (null = ไม่มี) */
export const fbGet = async (path, query) => {
  const response = await send("GET", path, { query });
  if (!response.ok) throw await failFrom(response);
  return response.json();
};

/** เขียนทับทั้ง node */
export const fbPut = async (path, value) => {
  const response = await send("PUT", path, { body: value });
  if (!response.ok) throw await failFrom(response);
  return response.json();
};

/** อัปเดตหลายคีย์พร้อมกัน (multi-path) — ค่า null = ลบคีย์นั้น */
export const fbPatch = async (path, value) => {
  const response = await send("PATCH", path, { body: value });
  if (!response.ok) throw await failFrom(response);
  return response.json();
};

export const fbDelete = async (path) => {
  const response = await send("DELETE", path);
  if (!response.ok) throw await failFrom(response);
  return null;
};

/**
 * อ่านลูกที่ field == value (ต้องมี .indexOn ที่ field ใน rules)
 * ถ้า rules ยังไม่ได้วาง index จะถอยไปอ่านทั้ง node แล้วกรองฝั่งหน้าเว็บ (ช้ากว่า แต่ถูกต้อง)
 */
export const fbQueryEqual = async (path, field, value) => {
  const query = { orderBy: JSON.stringify(field), equalTo: JSON.stringify(value) };
  const response = await send("GET", path, { query });
  if (response.ok) return response.json();

  const err = await failFrom(response);
  if (response.status === 400 && /index/i.test(err.message)) {
    const all = await fbGet(path);
    if (!all || typeof all !== "object") return all;
    const out = {};
    Object.entries(all).forEach(([k, v]) => {
      if (v && typeof v === "object" && String(v[field]) === String(value)) out[k] = v;
    });
    return out;
  }
  throw err;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * transaction แบบ ETag (compare-and-set) — ใช้กับตัวนับเลขที่เอกสาร
 * อ่านค่า + ETag -> update(current) คืนค่าใหม่ -> PUT พร้อม if-match
 * ถ้ามีคนเขียนแทรก (412) จะอ่านใหม่แล้วลองอีก ไม่เขียนทับ
 * update คืน undefined = ไม่เขียน
 */
export const fbTransact = async (path, update, { retries = 15 } = {}) => {
  for (let attempt = 0; attempt < retries; attempt += 1) {
    const read = await send("GET", path, { headers: { "X-Firebase-ETag": "true" } });
    if (!read.ok) throw await failFrom(read);
    const etag = read.headers.get("ETag");
    if (!etag) throw new FbError("Firebase ไม่ส่ง ETag กลับมา — ออกเลขที่เอกสารแบบไม่ซ้ำไม่ได้");
    const current = await read.json();

    const next = update(current);
    if (next === undefined) return current;

    const write = await send("PUT", path, { body: next, headers: { "if-match": etag } });
    if (write.ok) return next;
    if (write.status !== 412) throw await failFrom(write);

    await sleep(Math.min(400, 15 * 2 ** Math.min(attempt, 5)) * (0.5 + Math.random()));
  }
  throw new FbError("ระบบกำลังออกเลขพร้อมกันหลายรายการ กรุณาลองใหม่อีกครั้ง");
};
